import { useState, useEffect } from "react";
import { WifiOff, Wifi, RefreshCw, CheckCircle2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useI18n } from "@/lib/i18n";

type SyncStatus = "online" | "offline" | "syncing" | "synced";

const OfflineIndicator = () => {
  const { t } = useI18n();
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>(navigator.onLine ? "online" : "offline");
  const [showSynced, setShowSynced] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      setSyncStatus("syncing");
      // Simulate sync completion
      setTimeout(() => {
        setSyncStatus("synced");
        setShowSynced(true);
        setTimeout(() => {
          setShowSynced(false);
          setSyncStatus("online");
        }, 3000);
      }, 1500);
    };
    const handleOffline = () => {
      setIsOffline(true);
      setSyncStatus("offline");
    };
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const shouldShow = isOffline || syncStatus === "syncing" || showSynced;

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          initial={{ y: -40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -40, opacity: 0 }}
          className={`fixed top-0 left-0 right-0 z-[100] py-2 px-4 text-center text-sm flex items-center justify-center gap-2 transition-colors ${
            isOffline
              ? "bg-destructive text-destructive-foreground"
              : syncStatus === "syncing"
              ? "bg-amber-500 text-white"
              : "bg-primary text-primary-foreground"
          }`}
        >
          {isOffline && (
            <>
              <WifiOff className="h-4 w-4" />
              {t("offline.message")}
            </>
          )}
          {syncStatus === "syncing" && (
            <>
              <RefreshCw className="h-4 w-4 animate-spin" />
              {t("offline.syncing")}
            </>
          )}
          {showSynced && !isOffline && syncStatus === "synced" && (
            <>
              <CheckCircle2 className="h-4 w-4" />
              {t("offline.synced")}
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default OfflineIndicator;
